import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MultiSubscriptionComponent } from '@ddv/common-components';
import { MetadataService } from '@ddv/datasets';
import {
    ChartConfigs,
    DataConfigSummary,
    DatasetMetadata,
    FieldMetadata,
    FieldMetadataGroup,
    GridConfigs,
    MetadataLookup,
    SummaryColumnFilter,
    SummaryConfig,
    VisualizationType,
    VizConfigs,
} from '@ddv/models';

import { Configure } from './manage-widgets.model';
import { DropZoneConfig } from '../../../models/drop-zone-config';
import { FieldMetadataService } from '../../../services/field-metadata.service';
import { DragStateEvent } from '../field-picker-tree/field-picker-tree.component';

@Component({
    template: '',
    standalone: false,
})
export abstract class ConfigureComponent extends MultiSubscriptionComponent implements Configure {
    @Input() isAttached = true;
    @Input() isReadOnly = false;
    @Output() updatePreview = new EventEmitter();

    private readonly metadataService = inject(MetadataService);
    private readonly fieldMetadataService = inject(FieldMetadataService);

    activelyDragging = false;
    dropZones: DropZoneConfig[] = [];
    searchValue: string | undefined;
    metadata: FieldMetadata[] = [];
    groupedMetadata: FieldMetadataGroup[] | undefined;
    dataConfigs: ChartConfigs | { summary: DataConfigSummary, filters: SummaryColumnFilter[] }[] | undefined;
    protected widgetId: number | undefined;
    protected visualizationType: VisualizationType | undefined;
    protected clientCode = '';

    // Though the name is similar, is is NOT @angular/core/OnInit. This is FB-custom
    onInit(
        widgetId: number,
        configs: VizConfigs | SummaryConfig,
        visualizationType: VisualizationType,
        clientCode: string,
    ): void {
        this.widgetId = widgetId;
        this.visualizationType = visualizationType;
        this.clientCode = clientCode;

        this.dropZones = this.getDropZones();
        if (configs) {
            this.setDataConfigs(configs);
        } else {
            this.initDataConfigs();
        }
        this.setMetadata();
    }

    dragStateChanged(event: DragStateEvent): void {
        if (event.state === 'start') {
            this.activelyDragging = true;
            this.dragStarted(event.data as FieldMetadata);
            return;
        }

        this.activelyDragging = false;
        this.dragEnded();
    }

    abstract dragStarted(metadata: FieldMetadata): void;

    // this function takes the metadata hash for each column and adds to it:  value=columnId and then turns it into an array
    setMetadata(): void {
        this.subscribeTo(this.metadataService.metadataState$, (param: DatasetMetadata) => {
            if (param.lastChangedWidgetId === this.widgetId) {
                this.processFieldMetadata(param.metadata.get(this.widgetId ?? 0));
            }
        });
    }

    processFieldMetadata(fieldMetadata?: MetadataLookup): void {
        if (!fieldMetadata) {
            return;
        }

        this.groupedMetadata = this.fieldMetadataService.convertToTreeByHierarchy(fieldMetadata);
        this.metadata = Object.keys(fieldMetadata).map((key) => fieldMetadata[key]);

        this.afterMetadataUpdate();
    }

    renderPreview(): void {
        this.updatePreview.emit();
    }

    abstract getSelectedConfigs(): ChartConfigs | GridConfigs | Partial<SummaryConfig>;

    abstract initDataConfigs(): void;

    abstract hasRequiredFields(): boolean;

    protected abstract getDropZones(): DropZoneConfig[];

    protected abstract setDataConfigs(configs: VizConfigs | SummaryConfig): void;

    protected abstract afterMetadataUpdate(): void;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected dragEnded(): void {}
}
